/* Settings sections from StudioCore desktop (m11 #8): the account modal (language, country / region, sign-in
 * method, sign-out confirm), the gear menu (Print history, Take the tour) and SupportModal (ticket + diagnostics).
 * Kit components; data on brightivity.dev (/auth/support/submit, /bridge/print-logs, /auth/me/school-state). */
import { useEffect, useMemo, useState } from 'react';
import './settings.css';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useSession } from '../../app/session.jsx';
import { APP_VERSION } from '../../config.js';
import { LANGUAGES, setLanguage } from '../../i18n/index.js';
import i18n from '../../i18n/index.js';
import useCatalogContext from '../../services/useCatalogContext.js';
import { groupJurisdictions } from '../../components/home/StatePicker.jsx';
import { useDevices } from '../computers/computers.jsx';
import { Button, Card, EmptyState, Icon, Input, Modal, Select, Skeleton, Tag } from '../../ui/index.jsx';
import { useTranslation } from 'react-i18next';

/* ---------------------------------------------------------------- diagnostics */
// The last browser errors, for support tickets (desktop attaches its app log). Installed once from main.jsx.
const ERRORS = [];
export function installErrorLog() {
  const push = (m) => { ERRORS.push(`${new Date().toISOString()} ${String(m).slice(0, 500)}`); if (ERRORS.length > 50) ERRORS.shift(); };
  window.addEventListener('error', (e) => push(`error: ${e.message} @ ${e.filename || ''}:${e.lineno || ''}`));
  window.addEventListener('unhandledrejection', (e) => push(`unhandled: ${(e.reason && (e.reason.message || e.reason)) || ''}`));
}

/* ---------------------------------------------------------------- language + country / region */
export function LanguageRegion() {
  const { t } = useTranslation('bv');
  const [lang, setLang] = useState(i18n.resolvedLanguage || i18n.language || 'en');
  const { jurisdiction, setJurisdiction, jurisdictions } = useCatalogContext();
  const { us, countries } = useMemo(() => groupJurisdictions(jurisdictions), [jurisdictions]);
  return (
    <Card className="bv-settings__card bv-settings__card--col" data-testid="language-region">
      <h2 className="bv-section-title">{t('settings.languageRegion')}</h2>
      <label className="bv-label" htmlFor="bv-lang">{t('settings.language')}</label>
      <Select id="bv-lang" value={lang} onChange={async (e) => { setLang(e.target.value); await setLanguage(e.target.value); }}
        options={LANGUAGES.map((l) => ({ value: l.code, label: l.label }))} data-testid="language-select" />
      <label className="bv-label" htmlFor="bv-region">{t('settings.region')}</label>
      <div className="bv-field bv-field--select"><select id="bv-region" className="bv-select" value={jurisdiction || ''} onChange={(e) => setJurisdiction(e.target.value || null)} data-testid="region-select">
        <option value="">{t('settings.notSet')}</option>
        {countries.length > 0 && <optgroup label={t('settings.countries')}>{countries.map((j) => <option key={j.code} value={j.code}>{j.name}</option>)}</optgroup>}
        <optgroup label={t('settings.unitedStates')}>{us.map((j) => <option key={j.code} value={j.code}>{j.name}</option>)}</optgroup>
      </select><Icon name="down" size={20} className="bv-field__chevron" /></div>
      <p className="bv-small bv-muted">{t('settings.regionHint')}</p>
    </Card>
  );
}

/* ---------------------------------------------------------------- sign-in method + sign-out confirm */
const METHOD = { email: 'settings.method.email', email_otp: 'settings.method.email', google: 'Google', microsoft: 'Microsoft', apple: 'Apple', canva: 'Canva', license: 'settings.method.license' };
export const methodLabel = (m) => { const k = METHOD[String(m || '').toLowerCase()]; return k ? (k.includes('.') ? i18n.t(`bv:${k}`) : k) : (m ? String(m) : i18n.t('bv:settings.method.unknown')); };

export function SignOutButton() {
  const { t } = useTranslation('bv');
  const { signOut } = useSession();
  const navigate = useNavigate();
  const [ask, setAsk] = useState(false);
  return (
    <>
      <Button icon="arrow" onClick={() => setAsk(true)} data-testid="sign-out">{t('shell.signOut')}</Button>
      <Modal open={ask} title={t('settings.signOut.title')} onClose={() => setAsk(false)}>
        <p className="bv-muted">{t('settings.signOut.body')}</p>
        <div className="bv-modal-actions">
          <Button onClick={() => setAsk(false)}>{t('settings.signOut.stay')}</Button>
          <Button variant="coral" icon="arrow" onClick={() => { setAsk(false); signOut(); navigate('/sign-in'); }} data-testid="confirm-sign-out">{t('shell.signOut')}</Button>
        </div>
      </Modal>
    </>
  );
}

/* ---------------------------------------------------------------- print history */
const when = (iso) => new Date(iso).toLocaleString(i18n.language, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const cleanPaper = (p) => String(p || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
const STATUS = { done: ['status.printed', 'green'], error: ['settings.history.failed', 'coral'], cancelled: ['status.cancelled', 'yellow'], abandoned: ['settings.history.noAnswer', 'yellow'] };

export function PrintHistoryModal({ open, onClose }) {
  const { t } = useTranslation('bv');
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const [more, setMore] = useState(false);
  const load = async (offset = 0) => {
    const r = await api(`/bridge/print-logs?limit=50&offset=${offset}`);
    if (!r.ok) { setErr(r.error); setItems((x) => x || []); return; }
    const got = r.data.items || [];
    setItems((x) => (offset ? [...(x || []), ...got] : got));
    setMore(got.length === 50);
  };
  useEffect(() => { if (open) { setItems(null); setErr(null); load(0); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Modal open={open} title={t('shell.printHistory')} onClose={onClose} width={760}>
      <div className="bv-history" data-testid="print-history">
        {!items ? <Skeleton height={200} radius={18} /> : err && !items.length ? <p className="bv-error-text">{err}</p>
          : items.length === 0 ? <EmptyState icon="print" title={t('settings.history.emptyTitle')}>{t('settings.history.emptyBody')}</EmptyState> : (
            <ul className="bv-history__list">
              {items.map((x) => {
                const s = STATUS[x.status] ? [t(STATUS[x.status][0]), STATUS[x.status][1]] : [x.status, 'blue'];
                return (
                  <li key={x.id} className="bv-history__row" data-testid="history-row">
                    <div className="bv-history__main">
                      <b>{x.name || t('common.poster')}</b>
                      <span className="bv-small bv-muted">{[x.printer, cleanPaper(x.paper), x.size, x.copies > 1 ? t('print.copies', { count: x.copies }) : null, x.computer].filter(Boolean).join(' · ')}</span>
                      {x.error && <span className="bv-small bv-error-text">{x.error}</span>}
                    </div>
                    <div className="bv-history__side"><Tag color={s[1]}>{s[0]}</Tag><span className="bv-small bv-muted">{when(x.at)}</span></div>
                  </li>
                );
              })}
            </ul>
          )}
        {more && <Button onClick={() => load(items.length)}>{t('common.showMore')}</Button>}
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- support */
const COMPONENTS = [
  { value: 'general', label: 'settings.support.about.general' },
  { value: 'print_preview', label: 'nav.print' },
  { value: 'print_editor', label: 'nav.design' },
  { value: 'print_browser', label: 'settings.support.about.canva' },
  { value: 'print_library', label: 'library.title' },
  { value: 'printables', label: 'nav.printables' },
  { value: 'scan', label: 'nav.scan' },
  { value: 'school', label: 'nav.school' },
  { value: 'helper', label: 'settings.support.about.helper' },
  { value: 'login', label: 'settings.support.about.login' },
];

export function SupportModal({ open, onClose, initialComponent = 'general' }) {
  const { t } = useTranslation('bv');
  const { devices } = useDevices(60000);
  const [subject, setSubject] = useState('');
  const [component, setComponent] = useState(initialComponent);
  const [printer, setPrinter] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { if (open) { setSubject(''); setComponent(initialComponent); setPrinter(''); setMessage(''); setDone(null); setErr(null); } }, [open, initialComponent]);
  const printers = useMemo(() => {
    const seen = new Set();
    return (devices || []).flatMap((d) => (d.printerList || []).map((p) => p.model)).filter((m) => m && !seen.has(m) && seen.add(m));
  }, [devices]);
  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) { setErr(t('settings.support.required')); return; }
    setBusy(true); setErr(null);
    // Diagnostics (desktop sends app version + its error log): app version, browser, computers + Helper versions, recent errors.
    const diag = [
      `Brightivity web ${APP_VERSION}`, `Browser: ${navigator.userAgent}`, `Page: ${location.pathname}`,
      ...(devices || []).map((d) => `Computer: ${d.name} (${d.os}, Helper ${d.engineVersion || '?'}, ${d.online ? 'online' : 'offline'}) printers: ${(d.printerList || []).map((p) => p.model).join(', ') || 'none'}`),
      ...ERRORS.slice(-20),
    ].join('\n');
    const r = await api('/auth/support/submit', { method: 'POST', body: { subject: subject.trim(), component, printer_model: printer || null, message: message.trim(), app_version: `Brightivity web ${APP_VERSION}`, error_logs: diag } });
    setBusy(false);
    if (r.ok && r.data && r.data.success) setDone(r.data.ticketId || true); else setErr(r.error || t('settings.support.failed'));
  };
  return (
    <Modal open={open} title={t('settings.contactSupport')} onClose={onClose} width={600}>
      {done ? (
        <div className="bv-support__done" data-testid="support-done">
          <Icon name="check" size={40} />
          <h3 className="bv-section-title">{t('settings.support.thanks')}</h3>
          <p className="bv-muted">{t('settings.support.reply')}</p>
          <Button variant="blue" onClick={onClose}>{t('common.done')}</Button>
        </div>
      ) : (
        <form className="bv-support" onSubmit={submit} data-testid="support-form">
          <label className="bv-label" htmlFor="bv-sup-subject">{t('settings.support.subject')}</label>
          <Input id="bv-sup-subject" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} placeholder={t('settings.support.subjectPlaceholder')} />
          <label className="bv-label" htmlFor="bv-sup-component">{t('settings.support.about.label')}</label>
          <Select id="bv-sup-component" value={component} onChange={(e) => setComponent(e.target.value)} options={COMPONENTS.map((c) => ({ ...c, label: t(c.label) }))} />
          <label className="bv-label" htmlFor="bv-sup-printer">{t('settings.support.printer')}</label>
          <Select id="bv-sup-printer" value={printer} onChange={(e) => setPrinter(e.target.value)} options={[{ value: '', label: printers.length ? t('settings.support.notPrinter') : t('settings.support.noPrinters') }, ...printers.map((m) => ({ value: m, label: m }))]} />
          <label className="bv-label" htmlFor="bv-sup-message">{t('settings.support.what')}</label>
          <textarea id="bv-sup-message" className="bv-textarea" rows={6} value={message} maxLength={10000} onChange={(e) => setMessage(e.target.value)} placeholder={t('settings.support.whatPlaceholder')} />
          <p className="bv-small bv-muted">{t('settings.support.diag')}</p>
          {err && <p className="bv-error-text" role="alert">{err}</p>}
          <div className="bv-modal-actions">
            <Button type="button" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="blue" icon="arrow" disabled={busy} data-testid="support-send">{busy ? t('print.card.sending') : t('common.send')}</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
